import { Box, useMantineTheme } from '@mantine/core';
import { ResponsiveBar } from '@nivo/bar';
// @ts-expect-error
import { nice, ticks } from 'd3-array';
import { useProfile } from 'init/use-enforce-profile';
import { tKeysLoose, useTranslation } from 'locales';
import { clone, initial, last, mergeWith, subtract } from 'lodash-es';
import { isNullish } from 'remeda';
import { BarChartTooltip } from 'shared/charts';
import type { AgedBalanceCompact } from 'shared/hooks/use-load-aged-balance';
import { useAgedBalanceColors } from 'shared/hooks/utils/use-aged-balance-colors';
import {
  useLocalizedCurrencyFormatter,
  useSafeLocalizedCompactCurrencyFormatter,
} from 'shared/utils/currency';

const DESIRED_X_AXIS_TICKS = 6;

interface AgedBalanceChartProps {
  agedBalanceCompact?: Pick<AgedBalanceCompact, 'credits' | 'debits'>;
}

const ResponsiveBarTooltip = ({ data: { value } }: any) => {
  const formatter = useLocalizedCurrencyFormatter();

  setTimeout(() => {
    const tooltip = document.getElementById('tooltip-fix');
    if (tooltip) tooltip.style.opacity = '1';
  }, 50);

  return (
    <BarChartTooltip id="tooltip-fix" style={{ opacity: 0, transition: 'all 0.1s ease-in-out' }}>
      {formatter.format(value)}
    </BarChartTooltip>
  );
};

export const AgedBalanceCompactChart = ({ agedBalanceCompact }: AgedBalanceChartProps) => {
  const { t } = useTranslation();
  const theme = useMantineTheme();
  const profile = useProfile();
  const colors = useAgedBalanceColors();
  const compactFormatter = useSafeLocalizedCompactCurrencyFormatter();
  const formatter = useLocalizedCurrencyFormatter();

  if (isNullish(agedBalanceCompact)) return null;

  const netAgedBalance = mergeWith(
    clone(agedBalanceCompact.debits),
    agedBalanceCompact.credits,
    subtract,
  );

  const barData = Object.entries(netAgedBalance).map(([key, value]) => ({
    value,
    label: key,
    color: colors[key as keyof typeof colors],
  })) as Array<{ label: string; color: string; value: number }>;

  // Insert the total due amount which is not provided as such by the API
  const overdueAmount =
    netAgedBalance['0'] + netAgedBalance['30'] + netAgedBalance['60'] + netAgedBalance['90'];
  barData.splice(0, 0, { value: overdueAmount, label: 'totalDue', color: colors.totalBalance });

  const barDataOrdered = [last(barData), ...initial(barData)].reverse() as typeof barData;

  const maxValue = Math.max(...barDataOrdered.map(bar => bar.value));
  const minValue = Math.min(...barDataOrdered.map(bar => bar.value));

  const [rangeStart, rangeEnd] = nice(Math.min(0, minValue), maxValue, DESIRED_X_AXIS_TICKS);
  const xValues = ticks(rangeStart, rangeEnd, DESIRED_X_AXIS_TICKS);

  return (
    <Box w="100%" h={460}>
      <ResponsiveBar
        colors={barDatum => barDatum.data.color}
        margin={{ bottom: 25, left: profile.locale === 'nl' ? 150 : 120, right: 95 }}
        layout="horizontal"
        data={barDataOrdered}
        indexBy="label"
        keys={['value']}
        valueScale={{ type: 'linear', min: rangeStart }}
        markers={[{ axis: 'x', value: 0, lineStyle: { stroke: theme.colors.gray[3] } }]}
        padding={0.42}
        isInteractive
        colorBy="indexValue"
        label={d => formatter.format(d.value!)}
        enableGridX
        enableGridY={false}
        labelSkipWidth={85}
        labelTextColor={theme.colors.gray[0]}
        tooltip={ResponsiveBarTooltip}
        axisBottom={{
          tickSize: 0,
          tickValues: xValues,
          format: val => compactFormatter.format(val).replace(/(\.[0]+|,[0]+)$/, ''),
        }}
        axisLeft={{
          tickPadding: xValues[0] < 0 ? 25 : 15,
          tickSize: 0,
          format: val => t(tKeysLoose.ANALYTICS.AGED_BALANCE[val]),
        }}
        borderRadius={4}
        animate={false}
        theme={{
          grid: {
            line: {
              stroke: theme.colors.gray[3],
              strokeDasharray: 7,
              strokeDashoffset: 15,
            },
          },
          labels: {
            text: {
              fontSize: 15,
              fontWeight: 500,
              fill: theme.colors.gray[6],
            },
          },
          text: {
            fontSize: 14,
            fill: theme.colors.gray[6],
          },
        }}
        gridXValues={xValues}
        layers={[
          'grid',
          'axes',
          'markers',
          'bars',
          ({ bars, innerWidth }) => {
            const [, secondBar, thirdBar] = [...bars].reverse();
            const secondBarEndPositionY = secondBar.y + secondBar.height;
            const halfwayPointY = (secondBarEndPositionY + thirdBar.y) / 2;

            return (
              <line
                x1={-65}
                y1={halfwayPointY}
                x2={innerWidth + 65}
                y2={halfwayPointY}
                stroke="#CED4DA"
                strokeWidth="1.5"
                strokeDasharray={7}
              />
            );
          },
          // See https://github.com/plouc/nivo/issues/146#issuecomment-1009184119
          ({ bars, labelSkipWidth }) => (
            <g>
              {bars.map(({ x, width, height, y, data }) =>
                width < labelSkipWidth ? (
                  <text
                    key={data.value}
                    fontSize={16}
                    fontWeight={500}
                    fill={theme.colors.gray[6]}
                    transform={`translate(${x + width + 10}, ${y + height / 2})`}
                    textAnchor="start"
                    dominantBaseline="central"
                  >
                    {formatter.format(data.value!)}
                  </text>
                ) : null,
              )}
            </g>
          ),
        ]}
      />
    </Box>
  );
};
